/**
 * Codex CLI Parser
 *
 * Parses OpenAI Codex CLI commands for adding MCP servers.
 *
 * Supported syntax:
 * - codex mcp add <server-name> [--env VAR=VALUE...] -- <command> [args...]
 *
 * Options:
 * - --env: VAR=VALUE (repeatable)
 *
 * Notes:
 * - Codex always uses STDIO transport
 * - The -- separator is required before the command
 * - OAuth is handled via separate `codex mcp login <name>` command
 */

import type { CliParseOptions, CliParseResult, ParsedCliFlags } from '../types';
import { createEmptyParsedFlags } from '../types';
import { BaseCliParser } from './base-parser';

class CodexCliParser extends BaseCliParser {
  readonly tool = 'codex' as const;

  readonly displayName = 'Codex CLI';

  readonly prefixes = ['codex mcp add'];

  parse({ tokens }: CliParseOptions): CliParseResult {
    const warnings: string[] = [];

    // Skip prefix tokens: codex, mcp, add
    const args = tokens.slice(3);
    if (args.length === 0) {
      return this.failResult('Missing server name and command');
    }

    const flags = CodexCliParser.parseFlags(args, warnings);

    // Codex always uses STDIO
    flags.transport = 'stdio';

    // Validate that we have a command (Codex requires -- <command>)
    if (!flags.command) {
      // Be lenient - if there's no --, try to parse as direct command
      if (flags.args.length === 0) {
        return this.failResult(
          'Codex MCP add requires a command. Use: codex mcp add <name> -- <command> [args...]',
        );
      }
    }

    return this.buildResult(flags, warnings);
  }

  /**
   * Parse flags and positional arguments from the command
   */
  private static parseFlags(args: string[], warnings: string[]): ParsedCliFlags {
    const flags = createEmptyParsedFlags();
    let i = 0;
    let sawDoubleDash = false;
    const positionalArgs: string[] = [];

    while (i < args.length) {
      const arg = args[i];

      if (sawDoubleDash) {
        // After --, everything is the command and its args
        if (!flags.command) {
          flags.command = arg;
        } else {
          flags.args.push(arg);
        }
        i += 1;
      } else if (arg === '--') {
        sawDoubleDash = true;
        i += 1;
      } else if (arg === '--env') {
        // Environment variable flag
        i += 1;
        const envVar = args[i];
        const parsed = BaseCliParser.parseEnvVar(envVar);
        if (parsed) {
          flags.env[parsed.key] = parsed.value;
        } else {
          warnings.push(`Invalid env format: ${envVar}. Expected KEY=VALUE.`);
        }
        i += 1;
      } else if (arg.startsWith('--')) {
        // Unknown flag with value
        const nextArg = args[i + 1];
        if (nextArg && !nextArg.startsWith('-')) {
          flags.unknownFlags.push(arg);
          i += 2;
        } else {
          flags.unknownFlags.push(arg);
          i += 1;
        }
      } else {
        // Positional argument (before --)
        positionalArgs.push(arg);
        i += 1;
      }
    }

    // First positional arg is the name
    const [name, secondArg, ...restArgs] = positionalArgs;
    if (name !== undefined) {
      flags.name = name;
    }

    // If there was no --, remaining positional args might be command + args
    // This is a lenient parsing mode for commands without --
    if (!sawDoubleDash && secondArg !== undefined) {
      warnings.push('Missing "--" separator before command. Parsing anyway.');
      flags.command = secondArg;
      flags.args = restArgs;
    }

    return flags;
  }
}

// Export singleton instance
export const codexParser = new CodexCliParser();
